/************
 * Service Class
 * File Name ...: cases.service.ts
 * Generated By : ianday on 10/13/23
 * Date ........: 10/13/23
 /************/
import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';
import { Settings } from './settings';
import { Case } from '../models/case';
import { CaseStatus } from '../models/case-status';
import { MemberDto } from '../models/member-dto';
import { RelationshipTypes } from "../models/relationshiptypes";
import { HouseholdMember } from '../models/household-member';
import { ServiceRequestMemberHistory } from '../models/service-request-member-history';

@Injectable({
	providedIn: 'root'
})
export class CasesService {

	public URLPath: string = Settings.getInstance().BASE_URL + 'cases/';
	public RelTypesURLPath: string = Settings.getInstance().BASE_URL + 'cases/relationshiptypes';
	public ServiceRequestMemberMappingsURL: string = Settings.getInstance().BASE_URL + 'cases/service-request-member-mappings/';
	public MemberHistoryURL: string = Settings.getInstance().BASE_URL + 'service-request-member-history/';

	public refreshEvent: EventEmitter<void> = new EventEmitter<void>();

	constructor(private http: HttpClient) {
	}
	getCasesByStatus(status: string): Observable<Case[]> {
		return this.http.get<Case[]>(`${this.URLPath}status/${status}`)
			.pipe(
				catchError((error: any) => {
					throw error;
				})
			);
	}

	searchMember(searchString: string): Observable<HouseholdMember[]> {
		return this.http.post<HouseholdMember[]>(`${this.URLPath}members/search`, { searchString })
			.pipe(
				catchError((error: any) => {
					return throwError(error);
				})
			);
	}

	getOpenCasesByAgency(agencyId: number, duration?: number): Observable<Case[]> {
		const url = duration
			? `${this.URLPath}open/${agencyId}/${duration}`
			: `${this.URLPath}open/${agencyId}/0`;

		return this.http.get<Case[]>(url).pipe(
			catchError((error: any) => {
				throw error;
			})
		);
	}

	getCompletedCasesWithinDays(agencyId: number, days: number): Observable<MemberDto[]> {
		return this.http.get<MemberDto[]>(`${this.URLPath}completed/${agencyId}/${days}`)
			.pipe(
				catchError((error: any) => {
					throw error;
				})
			);
	}

	getCasesWithinDays(agencyId: number, days: number): Observable<Case[]> {
		return this.http.get<Case[]>(`${this.URLPath}all/${agencyId}/${days}`)
			.pipe(
				catchError((error: any) => {
					throw error;
				})
			);
	}


	// Method to handle GET request for retrieving all CaseStatuses
	getAllCaseStatuses(): Observable<CaseStatus[]> {
		return this.http.get<CaseStatus[]>(`${this.URLPath}case-statuses`)
			.pipe(
				catchError((error: any) => {
					throw error;
				})
			);
	}

	// Method to handle GET request for retrieving a CaseStatus by ID
	getCaseStatusById(id: number): Observable<CaseStatus> {
		return this.http.get<CaseStatus>(`${this.URLPath}case-statuses/${id}`)
			.pipe(
				catchError((error: any) => {
					throw error;
				})
			);
	}

	// Method to handle POST request for creating a new CaseStatus
	createCaseStatus(caseStatus: CaseStatus): Observable<CaseStatus> {
		return this.http.post<CaseStatus>(`${this.URLPath}case-statuses`, caseStatus)
			.pipe(
				catchError((error: any) => {
					throw error;
				})
			);
	}

	// Method to handle PUT request for updating a CaseStatus
	updateCaseStatus(id: number, caseStatus: CaseStatus): Observable<CaseStatus> {
		return this.http.put<CaseStatus>(`${this.URLPath}case-statuses/${id}`, caseStatus)
			.pipe(
				catchError((error: any) => {
					throw error;
				})
			);
	}

	// Method to handle DELETE request for deleting a CaseStatus
	deleteCaseStatus(id: number): Observable<void> {
		return this.http.delete<void>(`${this.URLPath}case-statuses/${id}`)
			.pipe(
				catchError((error: any) => {
					throw error;
				})
			);
	}

	// Method to search cases based on search parameters
	searchCases(searchParams: any): Observable<Case[]> {
		const url = `${this.URLPath}search`;
		return this.http.post<Case[]>(url, searchParams)
			.pipe(
				catchError((error: any) => {
					throw error;
				})
			);
	}
	updateStatusForCase(id: number, caseStatus: string): Observable<CaseStatus> {
		return this.http.get<any>(`${this.URLPath}status-for-case/${id}/${caseStatus}`)
			.pipe(
				catchError((error: any) => {
					throw error;
				})
			);
	}

	updateStatusForCaseRecord(id: number, caseStatus: string, caseRec: Case): Observable<CaseStatus> {
		return this.http.put<CaseStatus>(`${this.URLPath}status-for-case-record/${id}/${caseStatus}`, caseRec)
			.pipe(
				catchError((error: any) => {
					throw error;
				})
			);
	}

	updateCaseApplicant(caseId: number, record: any): Observable<any> {
		return this.http.put(this.URLPath + '/applicant-for-case/' + caseId, record)
			.pipe(
				retry(1)
			);
	}
	/** Relationship types **/
	getAllRelationshipTypes(): Observable<any> {
		return this.http.get<any>(this.RelTypesURLPath)
			.pipe(
				retry(1)
			);
	}

	createRelationshipTypes(record: RelationshipTypes): Observable<any> {
		return this.http.post(this.RelTypesURLPath, record)
			.pipe(
				retry(1)
			);
	}

	updateRelationshipTypes(record: RelationshipTypes): Observable<any> {
		return this.http.put(this.RelTypesURLPath + '/' + record.id, record)
			.pipe(
				retry(1)
			);
	}

	deleteRelationshipTypes(id: number): Observable<any> {
		return this.http.delete(this.RelTypesURLPath + '/' + id)
			.pipe(
				retry(1)
			);
	}

	// Method to handle GET request for retrieving all duplicate members
	getAllDuplicateMembers(duplicateKey: string, primaryMemberId: number): Observable<HouseholdMember[]> {
		const body = { duplicateKey, primaryMemberId };
		return this.http.post<HouseholdMember[]>(`${this.URLPath}members/duplicates`, body)
			.pipe(
				catchError((error: any) => {
					throw error;
				})
			);
	}
	// Method to handle PUT request for updating the duplicate status for a member
	updateDuplicateStatusForMember(memberId: number, newStatus: string): Observable<HouseholdMember> {
		return this.http.put<HouseholdMember>(`${this.URLPath}members/duplicate-status/${memberId}`, { status: newStatus });

	}

	// Method to handle PUT request for updating the duplicate member mapping
	replaceCaseMemberWithAnotherMember(memberId: number, newMemberId: number, caseId: number): Observable<HouseholdMember> {
		const requestBody = {
			memberId: memberId,
			newMemberId: newMemberId,
			caseId: caseId
		};

		return this.http.put<HouseholdMember>(`${this.URLPath}members/duplicates/replace-case-member`, requestBody);
	}

	// Method to handle PUT request for updating the duplicate member mapping
	replaceMatchingMembersWithDominantMemberRecord(memberIds: number[], newMemberId: number): Observable<HouseholdMember> {
		const requestBody = {
			memberIds: memberIds,
			newMemberId: newMemberId,
		};

		return this.http.put<HouseholdMember>(`${this.URLPath}members/duplicates/suppress-members`, requestBody);
	}

	/** ----------- Service Request Member Mappings ----------- **/

	// Get all mappings for a given member
	getServiceRequestMemberMappingsByMemberId(memberId: number): Observable<any[]> {
		return this.http.get<any[]>(`${this.ServiceRequestMemberMappingsURL}member/${memberId}`)
			.pipe(catchError((error: any) => throwError(error)));
	}

	// Get all mappings for an array of memberIds
	getServiceRequestMemberMappingsByMemberIds(memberIds: number[]): Observable<any[]> {
		return this.http.post<any[]>(`${this.ServiceRequestMemberMappingsURL}by-members`, { memberIds })
			.pipe(catchError((error: any) => throwError(error)));
	}

	// Get all mappings for a given service request
	getServiceRequestMemberMappingsByServiceRequestId(serviceRequestId: number): Observable<any[]> {
		return this.http.get<any[]>(`${this.ServiceRequestMemberMappingsURL}request/${serviceRequestId}`)
			.pipe(catchError((error: any) => throwError(error)));
	}

	// Delete all mappings for a given service request
	deleteServiceRequestMemberMappingsByServiceRequestId(serviceRequestId: number): Observable<void> {
		return this.http.delete<void>(`${this.ServiceRequestMemberMappingsURL}request/${serviceRequestId}`)
			.pipe(catchError((error: any) => throwError(error)));
	}

	// Create a new service request-member mapping
	createServiceRequestMemberMapping(mappingData: any): Observable<any> {
		return this.http.post<any>(`${this.ServiceRequestMemberMappingsURL}`, mappingData)
			.pipe(catchError((error: any) => throwError(error)));
	}

	// Create multiple service request-member mappings
	createServiceRequestMemberMappings(mappings: any[]): Observable<any> {
		return this.http.post<any>(`${this.ServiceRequestMemberMappingsURL}bulk`, { mappings })
			.pipe(catchError((error: any) => throwError(error)));
	}

	/** ----------- Service Request Member History ----------- **/

	// Fetch all history records for a closed service request
	getMemberHistory(serviceRequestId: number): Observable<ServiceRequestMemberHistory[]> {
		return this.http.get<ServiceRequestMemberHistory[]>(`${this.MemberHistoryURL}${serviceRequestId}`)
			.pipe(catchError((error: any) => throwError(error)));
	}

	// Trigger on-demand snapshot for a service request (idempotent — skips if already exists).
	// Returns the resulting history records. Used for old requests that have no snapshot.
	triggerHistorySnapshot(serviceRequestId: number): Observable<ServiceRequestMemberHistory[]> {
		return this.http.post<ServiceRequestMemberHistory[]>(`${this.MemberHistoryURL}snapshot/${serviceRequestId}`, {})
			.pipe(catchError((error: any) => throwError(error)));
	}

	// Update the selected (checked/unchecked) flag on a single history record
	// PUT /:id  — spec: { selected: 0|1 }
	updateMemberHistorySelected(id: number, selected: number): Observable<ServiceRequestMemberHistory> {
		return this.http.put<ServiceRequestMemberHistory>(`${this.MemberHistoryURL}${id}`, { selected })
			.pipe(catchError((error: any) => throwError(error)));
	}

	// Update personal details of a history member record (clerical reopen edit)
	updateMemberHistoryRecord(record: ServiceRequestMemberHistory): Observable<ServiceRequestMemberHistory> {
		return this.http.put<ServiceRequestMemberHistory>(`${this.MemberHistoryURL}record/${record.id}`, record)
			.pipe(catchError((error: any) => throwError(error)));
	}

	// Add a new member directly into history (clerical reopen add)
	addMemberToHistory(record: ServiceRequestMemberHistory): Observable<ServiceRequestMemberHistory> {
		return this.http.post<ServiceRequestMemberHistory>(`${this.MemberHistoryURL}`, record)
			.pipe(catchError((error: any) => throwError(error)));
	}

	// Delete a history member record (clerical reopen delete)
	deleteMemberFromHistory(id: number): Observable<void> {
		return this.http.delete<void>(`${this.MemberHistoryURL}${id}`)
			.pipe(catchError((error: any) => throwError(error)));
	}

}
